import React, { forwardRef, useEffect, useState } from "react";
import Loader from "../components/icons/Loader.jsx";
import { getRestrictedFeatureCopy, usePortalContext } from "../context/PortalContext/portalcontext.jsx";
import { useSearchParams } from "react-router-dom";
import PortalMenu from "../components/PortalMenu/PortalMenu.jsx";
import { useNotificationActions } from "../context/NotificationsContext/NotificationsProvider.jsx";

// visiting a section clears its unread notifications
const TAB_NOTIFICATION_SCOPES = {
  "posted-events": "dashboard:posted-events",
};

//#region COMPONENTS
const Tabs = ({ children }) => {
  return <div className="w-full min-h-[calc(100vh-300px)]">{children}</div>;
};

//#endregion
export const PortalTitle = (props) => {
  const {
    portalMenuGroups,
    handlePortalMenuItemClick,
    currentTab,
    currentVenuePortalTab,
    selectedPortalMenuItemId: sharedTabSelectedItemId,
    selectedPortalMenuActionId: sharedSelectedActionId,
  } = usePortalContext();
  // the dashboard draws the menu once (Portal's shell) so it never moves between tabs or screen sizes;
  // tabs still call <PortalTitle /> but those render nothing
  if (!props.shell) return null;
  const selectedPortalMenuItemId =
    currentTab === "venues" && currentVenuePortalTab
      ? {
          new_venue: "new-venue",
          pending_venues: "pending-venues",
          claimed_venues: "claimed-venues",
        }[currentVenuePortalTab]
      : sharedTabSelectedItemId;
  const selectedPortalMenuActionId = sharedSelectedActionId;

  return (
    <div
      className={`mb-kit-8 w-full ${props.className || ""}`}
    >
      {props.showTitle && (
        <div
          {...props}
          className={`text-kit-2xl flex items-center justify-between font-['Primary-Bold'] text-start `}
        >
          {props.children}
        </div>
      )}
      <div className={props.showTitle ? "mt-kit-12" : ""}>
        <PortalMenu
          groups={portalMenuGroups}
          currentTab={currentTab}
          selectedItemId={selectedPortalMenuItemId}
          selectedActionId={selectedPortalMenuActionId}
          onSelect={handlePortalMenuItemClick}
        />
      </div>
    </div>
  );
};

const Portal = () => {
  // const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    hasPermission,
    locationLoading,
    currentTab,
    setCurrentTab,
    setTabFromUrl,
    tabsList,
    userLocation,
  } = usePortalContext();
  const { markScopeRead } = useNotificationActions();

  useEffect(() => {
    const scope = TAB_NOTIFICATION_SCOPES[currentTab];
    if (scope && markScopeRead) markScopeRead(scope);
  }, [currentTab, markScopeRead]);

  useEffect(() => {
    if (searchParams.get("tab")) {
      let tabFromUrl = searchParams.get("tab");
      // Map legacy tab names to current tab names
      if (tabFromUrl === "business-tools") {
        tabFromUrl = "calendar";
      } else if (tabFromUrl === "roster") {
        tabFromUrl = "bookings";
      }
      (setTabFromUrl || setCurrentTab)(tabFromUrl);
    } else if (hasPermission) {
      const firstTab = tabsList.find(
        (tb) => hasPermission[tb.permission] === true
      );
      if (firstTab) {
        (setTabFromUrl || setCurrentTab)(firstTab.index);
      }
    }
  }, [searchParams, hasPermission, setTabFromUrl, setCurrentTab]);

  if (locationLoading)
    return (
      <div className="min-h-[100vh] flex justify-center items-center w-full bg-[var(--inyc-primary)]">
        <Loader />
      </div>
    );

  const hasTabPermission = (perm, tab) => {
    if (hasPermission) {
      return hasPermission[perm] && tab.element({ key: tab.index });
    }
    return false;
  };

  return (
    <div style={styles.portalContainer}>
      {hasPermission === null ? (
        <div style={styles.loadingContainer}>
          <Loader />
        </div>
      ) : (
        <>
          {/* one container for every tab and state, with the menu always first, so it never shifts */}
          <div className="w-full min-w-0 relative min-h-[calc(100vh-110px)] bg-[var(--inyc-primary)] p-kit-16 md:pt-kit-40 pb-[calc(var(--inyc-bottom-navbar-height)+1rem)]">
            <PortalTitle shell />
            {(() => {
              const currentTabData = tabsList.find((tb) => tb.index === currentTab);
              if (!currentTabData) {
                return (
                  <div className="mt-[5rem] mx-auto px-kit-32 text-kit-md max-w-[500px] text-center">
                    The requested page could not be found.
                  </div>
                );
              }
              const hasPermissionForTab = hasTabPermission(
                currentTabData.permission,
                currentTabData
              );
              const restrictedCopy = getRestrictedFeatureCopy(currentTabData);

              if (!hasPermissionForTab) {
                return currentTabData.restrictedContent ? (
                  <div className="mt-kit-32 px-kit-16 w-full flex justify-center">
                    {currentTabData.restrictedContent()}
                  </div>
                ) : (
                  <div className="mt-[5rem] mx-auto px-kit-32 text-kit-md leading-[1.6rem] max-w-[500px] text-center text-[var(--inyc-primary-text-color-light)]">
                    {restrictedCopy.body}
                  </div>
                );
              }

              return (
                <Tabs selected={currentTab}>
                  {tabsList.map((tb) =>
                    currentTab === tb.index ? (
                      <>
                        {hasPermission[tb.permission] && (
                          <div index={tb.index}>
                            {userLocation && tb.element({ key: tb.index })}
                          </div>
                        )}
                      </>
                    ) : null
                  )}
                </Tabs>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  portalContainer: {
    // paddingTop: 50,
    width: "100%",
    minHeight: "100vh",
    height: "100%",
    maxWidth: "800px",
    display: "flex",

    margin: "auto",
    justifyContent: "center",
    // alignItems: "center",
  },
  portalContent: {
    width: "100%",
    minHeight: "calc(100vh - 110px)",
    // background: "radial-gradient(rgba(0,0,0,0.01),rgba(0,0,0,0.02))",
    paddingTop: 0,
    // paddingBottom: "var(--inyc-space-48)",
  },
  portalTabs: {
    width: "100%",
    // minHeight: "60px",
    // paddingTop: "var(--inyc-space-64)",
    paddingBottom: "var(--inyc-space-20)",
    backgroundColor: "var(--inyc-primary)",
    // boxShadow: "var(--inyc-shadow-card)",
    // boxShadow: "var(--inyc-shadow-sm)",
    borderBottom: "2px solid var(--inyc-divider)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "var(--inyc-space-10)",
  },
  TabPagesContainer: {
    width: "100%",
    minHeight: "calc(100vh - 300px)",
  },
};

export default Portal;
